import React, { useEffect, useState, useCallback } from "react";
import {
  applyLeave,
  getMyLeaves,
  cancelLeave
} from "../api/leaves/leavePortalApi";
import {
  getActiveLeaveTypes
} from "../api/payroll/leaveTypePortalApi";
import ApplyLeaveForm from "../components/leaves/ApplyLeaveForm";
import LeaveCard from "../components/leaves/LeaveCard";
import {
  Calendar
} from "lucide-react";

import {
  Card,
  CardContent,
  Skeleton
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const STATUS_TABS = ["ALL", "PENDING", "APPROVED", "REJECTED", "CANCELLED"];

export default function MyLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL");
  const toast = useToast();

  const showSuccess = (msg) => {
    toast.success(msg);
  };
  const showError = (msg) => {
    toast.error(msg);
  };

  const fetchLeaveTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const res = await getActiveLeaveTypes();
      const list = Array.isArray(res) ? res : res?.data || [];
      setLeaveTypes(list.filter((t) => t && t._id && t.active !== false));
    } catch (err) {
      showError(err.response?.data?.message || "Failed to load leave types");
      setLeaveTypes([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  const fetchMyLeaves = async () => {
    setLoading(true);
    try {
      const filters = activeTab === "ALL" ? {} : { status: activeTab };
      const res = await getMyLeaves(filters);
      const list = Array.isArray(res) ? res : res?.data || res?.leaves || [];
      setLeaves(list);
    } catch (err) {
      showError(err.response?.data?.message || "Failed to load your leaves");
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  useEffect(() => {
    fetchMyLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleApply = async (formData) => {
    setSubmitting(true);
    try {
      await applyLeave({
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason,
        attachmentUrl: formData.attachmentUrl || "",
      });
      showSuccess("Leave application submitted successfully");
      fetchMyLeaves();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to apply for leave");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this leave request?")) {
      return;
    }
    try {
      await cancelLeave(id);
      showSuccess("Leave request cancelled");
      fetchMyLeaves();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to cancel leave");
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            My Leave Portal
          </h1>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            Submit new leave applications and track request statuses.
          </p>
        </div>
      </div>

      {/* Global Alerts */}
      
      

      {/* Form Container */}
      <ApplyLeaveForm
        onSubmit={handleApply}
        loading={submitting}
        leaveTypes={leaveTypes}
        leaveTypesLoading={typesLoading}
      />

      {/* Filter Tabs */}
      <div className="flex gap-1.5 overflow-x-auto border-b border-neutral-200 dark:border-neutral-800 pb-2 scrollbar-none">
        {STATUS_TABS.map((status) => {
          const isActive = activeTab === status;
          return (
            <button
              key={status}
              type="button"
              onClick={() => setActiveTab(status)}
              className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${ isActive ? "bg-neutral-900 text-white shadow-xs" : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200/70" }`}
            >
              {status}
            </button>
          );
        })}
      </div>

      {/* Leave Application Feed */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton height={180} width="100%" />
          <Skeleton height={180} width="100%" />
          <Skeleton height={180} width="100%" />
        </div>
      ) : leaves.length === 0 ? (
        <Card className="border border-dashed border-neutral-200/80 shadow-none p-10 text-center">
          <CardContent className="p-0 text-xs text-neutral-400 dark:text-neutral-500">
            No leave requests found for the selected status.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leaves.map((leave) => (
            <LeaveCard
              key={leave._id}
              leave={leave}
              isOwn={true}
              onCancel={handleCancel}
            />
          ))}
        </div>
      )}
    </div>
  );
}